/**
 * Checks a custom question with Rio while it's typed (debounced), so a
 * question Rio won't ask is caught in place, with Rio's reason, rather than at
 * save. A check that fails to run never blocks typing or adding.
 */
import { useEffect, useRef, useState } from 'react';
import type {
  InterviewerConfig,
  SectionKey,
} from '../../services/interviewer.types';
import { useInterviewerService } from '../../interviewer/serviceContext';
import { addQuestion, updateQuestion } from '../../interviewer/model';

export const QUESTION_CHECK_MS = 500;

export const useQuestionCheck = (
  config: InterviewerConfig,
  text: string,
  section: SectionKey,
  /** The question being edited; absent while adding a new one. */
  editingId: string | null,
): string | null => {
  const service = useInterviewerService();
  const [problem, setProblem] = useState<string | null>(null);
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    setProblem(null);
    const clean = text.trim();
    if (!clean) return;
    let stale = false;
    const timer = setTimeout(async () => {
      const current = configRef.current;
      const candidate = editingId
        ? updateQuestion(current, editingId, { text: clean })
        : addQuestion(current, clean, section, '__checking__');
      const index = editingId
        ? candidate.customQuestions.findIndex((q) => q.id === editingId)
        : candidate.customQuestions.length - 1;
      if (index < 0 || candidate === current) return;
      try {
        const result = await service.validate(candidate);
        if (stale) return;
        const hit = result.problems.find((p) =>
          p.path.startsWith(`customQuestions[${index}]`),
        );
        setProblem(hit?.message ?? null);
      } catch {
        // A failed check says nothing; Rio checks again on save.
      }
    }, QUESTION_CHECK_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [text, section, editingId, service]);

  return problem;
};
